import { useContext, useEffect, useState, useRef, JSX } from "react";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { toast } from "react-toastify";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import PageSection from "@app/shared/PageSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import ExportService from "@app/exports/exportService";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { BulkTargetingWizardStepProps } from "@app/targeting/import/interfaces";
import { BulkTargetingWizardContext } from "@app/targeting/import/TargetingWizardContext";


const TargetingWizardImportList = (props: BulkTargetingWizardStepProps): JSX.Element => {
  const wizardContext = useContext(BulkTargetingWizardContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const exportService = new ExportService();
  const helpFileUri = useRef<string>(`${window.apiEndpointV2_0}/projectionproviders/bulktargetingsamplefile/csv`).current;
  const uploadObjectKey = "bulkTargetingFile";
  const [remainingProviderActionCount, setRemainingProviderActionCount] = useState<number>(null);
  const [bulkUploadLimit, setBulkUploadLimit] = useState<number>(0);
  const [isUploadSuccessful, setIsUploadSuccessful] = useState<boolean>(true);

  const handleFileUpload = async (data: FormData) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const response = await wizardContext.projectionProviderService.importBulkTargetFile(data);

        if (featureFlags.has(Features.BULK_UPLOAD_LIMIT)) {
          if (remainingProviderActionCount === 0 || response.uploadLimitationStatus < 0) {
            toast(getUploadLimitMessage(response.uploadLimitationStatus),
              {
                containerId: "z-index-9999",
                type: "error"
              });

            setIsUploadSuccessful(false);
            reject();
          } else {
            setIsUploadSuccessful(true);
            setRemainingProviderActionCount(response.uploadLimitationStatus);
            const validItems = <>{response.recordCount} Valid Records</>;
            wizardContext.setBulkImportFormData(data);
            wizardContext.setBulkProviderTargetId(response.bulkTargetId);
            wizardContext.setResultsSummary([{ validItems, [wafflesKeyColumnName]: "key" }]);

            resolve();
          }
        } else {
          const validItems = <>{response.recordCount} Valid Records</>;
          wizardContext.setBulkImportFormData(data);
          wizardContext.setBulkProviderTargetId(response.bulkTargetId);
          wizardContext.setResultsSummary([{ validItems, [wafflesKeyColumnName]: "key" }]);

          resolve();
        }
      } catch (error) {
        reject(error.responseJSON);
      }
    });
  };

  useEffect(() => {
    if (featureFlags.has(Features.BULK_UPLOAD_LIMIT)) {
      exportService.getVolumeActionRowCount(0).then((rowCount) => setRemainingProviderActionCount(rowCount));
      exportService.getVolumeActionUploadLimit().then((uploadLimit) => setBulkUploadLimit(uploadLimit));
    }
  }, []);

  useEffect(() => {
    props.stepIsValid(!!wizardContext.bulkImportFormData && !!wizardContext.bulkProviderTargetId && isUploadSuccessful);
  }, [wizardContext.bulkImportFormData, wizardContext.bulkProviderTargetId, isUploadSuccessful]);

  const getUploadLimitMessage = (status: number): string => {
    if (remainingProviderActionCount === 0 || status < 0) {
      return `Your Volume Action limit has been exceeded. Volume actions are limited to ${bulkUploadLimit} providers per day. Please try again later.`;
    }
  }

  const downloadSampleFile = () => {
    authenticatedDownload(helpFileUri, undefined);
  }

  return (
    <PageSection>
      <div className="container">
        <p className="mt-3">Please upload a CSV file. Find correct formatting and information needed  <QESButton qesType="secondary" className="bg-transparent p-0" qesTestId="import-targeting__sample-file" onClick={downloadSampleFile}>here</QESButton>.</p>
        {featureFlags.has(Features.BULK_UPLOAD_LIMIT) && remainingProviderActionCount !== null && <p className="mt-3">{`You have ${remainingProviderActionCount} provider actions left to use across Volume Targeting and Volume Terminations.`}</p>}
        <QESAlert type="info" classNames="mb-2" persist={true}>
          All specialty codes with leading zeros must have that reflected in the upload file.
        </QESAlert>
        <DragAndDropFileUploader apiKeyName={uploadObjectKey} onUpload={handleFileUpload} filetype=".csv" hasBeenSuccessful={!!wizardContext.bulkImportFormData} testId="import-terminations__file-input" />
      </div>
    </PageSection>
  );
};

export default TargetingWizardImportList;
